import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { QuizQuestion } from '../data/playgroundData';

interface QuizPanelProps {
  questions: QuizQuestion[];
  onComplete?: (score: number, total: number) => void;
}

const LABELS = ['A', 'B', 'C', 'D'];

function renderQuizText(text: string) {
  // Split by triple-backtick code blocks: ```lang\ncode\n```
  const parts = text.split(/(```[\s\S]*?```)/g);
  return parts.map((part, i) => {
    if (part.startsWith('```')) {
      const code = part.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
      return (
        <pre key={i} className="my-3 rounded-lg bg-[#1a1f2e] border border-gray-700/50 p-4 font-mono text-sm text-emerald-300 overflow-x-auto leading-relaxed whitespace-pre">
          {code}
        </pre>
      );
    }
    // Handle inline backticks within normal text
    const inlineParts = part.split(/(`[^`]+`)/g);
    return (
      <span key={i}>
        {inlineParts.map((seg, j) =>
          seg.startsWith('`') && seg.endsWith('`') ? (
            <code key={j} className="rounded bg-[#1a1f2e] border border-gray-700/50 px-1.5 py-0.5 font-mono text-emerald-300 text-[0.85em]">
              {seg.slice(1, -1)}
            </code>
          ) : (
            <span key={j}>{seg}</span>
          )
        )}
      </span>
    );
  });
}

export default function QuizPanel({ questions, onComplete }: QuizPanelProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [finished, setFinished] = useState(false);
  const completedRef = useRef(false);

  const currentQuestion = questions[currentIndex];
  const selectedIndex = answers[currentIndex] ?? -1;
  const hasAnswered = selectedIndex >= 0;

  const handleSelect = useCallback(
    (optionIndex: number) => {
      if (showResult) return;
      setAnswers(prev => {
        const next = [...prev];
        next[currentIndex] = optionIndex;
        return next;
      });
      setShowResult(true);
    },
    [currentIndex, showResult]
  );

  const goNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const already = answers[newIndex] !== undefined && answers[newIndex] >= 0;
      setShowResult(already);
    } else {
      setFinished(true);
    }
  }, [currentIndex, questions.length, answers]);

  const goBack = useCallback(() => {
    if (currentIndex <= 0) return;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const already = answers[newIndex] !== undefined && answers[newIndex] >= 0;
    setShowResult(already);
  }, [currentIndex, answers]);

  const handleChangeAnswer = useCallback(() => {
    setAnswers(prev => {
      const next = [...prev];
      delete next[currentIndex];
      return next;
    });
    setShowResult(false);
  }, [currentIndex]);

  const handleRetry = () => {
    setCurrentIndex(0);
    setAnswers([]);
    setShowResult(false);
    setFinished(false);
  };

  useEffect(() => {
    if (!onComplete || completedRef.current || questions.length === 0) return;
    const allAnswered = questions.every((_, i) => answers[i] !== undefined && answers[i] >= 0);
    if (allAnswered) {
      completedRef.current = true;
      const correct = answers.filter((a, i) => a === questions[i].correctIndex).length;
      onComplete(correct, questions.length);
    }
  }, [answers, questions, onComplete]);

  if (questions.length === 0) {
    return (
      <div className="bg-[#0d1117] rounded-lg border border-gray-800 p-6 text-center text-gray-400">
        No hay preguntas disponibles.
      </div>
    );
  }

  if (finished) {
    const correct = answers.filter((a, i) => a === questions[i].correctIndex).length;
    const total = questions.length;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    const scoreColor =
      pct > 75 ? 'text-green-400' : pct >= 50 ? 'text-yellow-400' : 'text-red-400';
    const wrongIndices = questions
      .map((_, i) => (answers[i] !== questions[i].correctIndex ? i : -1))
      .filter(i => i >= 0);

    return (
      <div className="bg-[#0d1117] rounded-lg border border-gray-800 p-6 text-gray-300">
        <div className="text-center mb-8">
          <p className={`text-4xl font-bold ${scoreColor} transition-colors duration-300`}>
            {correct}/{total}
          </p>
          <p className={`text-2xl mt-2 ${scoreColor} transition-colors duration-300`}>
            {pct}%
          </p>
        </div>

        <button
          onClick={handleRetry}
          className="w-full py-3 px-4 rounded-lg bg-[#161b22] border border-gray-700 hover:border-green-500/50 text-gray-300 hover:text-green-400 font-medium transition-all duration-300"
        >
          Reintentar
        </button>

        {wrongIndices.length > 0 && (
          <div className="mt-8 space-y-4">
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
              Respuestas incorrectas
            </p>
            <ul className="space-y-4">
              {wrongIndices.map(idx => (
                <li
                  key={idx}
                  className="p-4 rounded-lg bg-[#161b22] border border-gray-800 transition-all duration-300"
                >
                  <div className="text-base text-gray-300 mb-2">{renderQuizText(questions[idx].question)}</div>
                  <div className="text-sm text-green-400/90">
                    {renderQuizText(questions[idx].explanation)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const progress = ((currentIndex + (hasAnswered ? 1 : 0)) / questions.length) * 100;

  return (
    <div className="bg-[#0d1117] rounded-lg border border-gray-800 overflow-hidden">
      {/* Progress bar */}
      <div className="h-1 bg-gray-800">
        <div
          className="h-full bg-green-500 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={goBack}
            disabled={currentIndex === 0}
            aria-label="Pregunta anterior"
            title="Pregunta anterior"
            className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg border border-gray-700 bg-[#161b22] text-gray-300 transition-colors hover:border-green-500/50 hover:text-green-400 disabled:pointer-events-none disabled:opacity-30"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden>
              <path
                fillRule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <p className="text-xs text-gray-500">
            Pregunta {currentIndex + 1} de {questions.length}
          </p>
        </div>

        <div className="text-base text-gray-300 mb-6">{renderQuizText(currentQuestion.question)}</div>

        <div className="space-y-3">
          {currentQuestion.options.map((opt, i) => {
            const isSelected = selectedIndex === i;
            const isCorrect = i === currentQuestion.correctIndex;
            const isWrong = isSelected && !isCorrect;
            const showCorrect = showResult && isCorrect;

            let borderClass = 'border-gray-800 hover:border-green-500/50';
            let bgClass = 'bg-[#161b22]';

            if (showResult) {
              if (showCorrect) {
                borderClass = 'border-green-500';
                bgClass = 'bg-green-500/20';
              } else if (isWrong) {
                borderClass = 'border-red-500';
                bgClass = 'bg-red-500/20';
              }
            } else if (isSelected) {
              borderClass = 'border-green-500/50';
            }

            return (
              <button
                key={i}
                type="button"
                onClick={() => handleSelect(i)}
                disabled={showResult}
                className={`w-full text-left p-4 rounded-lg border transition-all duration-300 ${borderClass} ${bgClass} text-sm text-gray-300 disabled:cursor-default`}
              >
                <span className="font-medium text-gray-400 mr-2">{LABELS[i]}.</span>
                <span>{renderQuizText(opt)}</span>
              </button>
            );
          })}
        </div>

        {showResult && (
          <div className="mt-6 p-4 rounded-lg bg-[#161b22] border border-gray-800">
            <p className="text-sm text-gray-400 mb-1">Explicación</p>
            <div className="text-sm text-gray-300">{renderQuizText(currentQuestion.explanation)}</div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleChangeAnswer}
                className="py-2 px-4 rounded-lg bg-[#0d1117] border border-gray-600 text-gray-300 hover:border-amber-500/50 hover:text-amber-200 transition-all duration-300 text-sm font-medium"
              >
                Cambiar respuesta
              </button>
              <button
                type="button"
                onClick={goNext}
                className="py-2 px-4 rounded-lg bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all duration-300 text-sm font-medium"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
